import { useState, useEffect, useRef, useCallback } from 'react'
import { formatTime } from '../utils/videocall/formatTime'

/**
 * Custom hook for call timer
 * @returns {Object} Timer state and controls
 */
export function useCallTimer() {
  const [seconds, setSeconds] = useState(0)
  const [isRunning, setIsRunning] = useState(false)
  const intervalRef = useRef(null)
  const startTimeRef = useRef(null)

  /**
   * Start the timer
   */
  const start = useCallback(() => {
    if (!isRunning) {
      startTimeRef.current = Date.now() - seconds * 1000
      setIsRunning(true)
    }
  }, [isRunning, seconds])

  /**
   * Stop the timer
   */
  const stop = useCallback(() => {
    setIsRunning(false)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  /**
   * Reset the timer
   */
  const reset = useCallback(() => {
    stop()
    setSeconds(0)
    startTimeRef.current = null
  }, [stop])

  /**
   * Get formatted time
   */
  const formattedTime = formatTime(seconds)

  // Timer effect
  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
        setSeconds(elapsed)
      }, 1000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isRunning])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop()
    }
  }, [stop])

  return {
    seconds,
    formattedTime,
    isRunning,
    start,
    stop,
    reset,
  }
}
